import { equal, handleAxiosError, notEqual } from '@/api/helpers';
import { ListingRequest } from '@/api/types';
import { client } from '@/client';
import { Account, ADEntityField } from '@/client/accounts';
import { AccountDomainStatus } from '@/client/accounts/types/account-domain.type';
import { Group } from '@/client/groups';
import { Role } from '@/client/roles/types';
import {
  Actions,
  Subjects,
  SystemRoles,
  UserFormState,
  UsernameLookupResult,
} from '@/client/users';
import { UserOrigin } from '@/client/users/types/origins.enum';
import {
  LoadingStateType,
  RedirectPaths,
  RedirectPathsEnum,
} from '@/common/constants';
import {
  availableLanguagesDropdown,
  courseOnlyLanguages,
} from '@/common/constants/languages';
import { roleTranslations } from '@/common/constants/roles';
import { InlineBlockEmails } from '@/components/block-emails';
import {
  FormikDropdown,
  FormikMultiSelect,
  FormikSwitch,
} from '@/components/form';
import { FormikIconInput } from '@/components/form/FormikIconInput';
import { FormikInput } from '@/components/form/FormikInput';
import { FormikLazyDropdown } from '@/components/form/FormikLazyDropdown';
import { PartialRequestMultiselect } from '@/components/form/generic';
import {
  createUserSchema,
  updateUserSchema,
} from '@/components/users/forms/validations';
import { useAccountDomains, useAccountUsers } from '@/hooks/query';
import { useAppSelector } from '@/hooks/store';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { usePermission } from '@/hooks/usePermission';
import { useToast } from '@/hooks/useToast';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUser } from '@/store/features/users';
import { DateFormats } from '@/system-settings/enums';
import { AppButton } from '@/ui/buttons';
import { AppCalendar } from '@/ui/calendar';
import { AppChip } from '@/ui/chip';
import { FlexContainer, FormContainer } from '@/ui/styled-ui';
import {
  branchAdminCheck,
  isDemoRestrictedMode,
  showBlockEmailsMessage,
} from '@/utils/helpers';
import { preventDropdownBrowserTranslate } from '@/utils/helpers/ui.helper';
import { PostHogFeatureFlags } from '@/utils/posthog';
import { AxiosError } from 'axios';
import { Field, Form, Formik, FormikValues } from 'formik';
import { debounce } from 'lodash';
import moment from 'moment';
import { DropdownChangeEvent } from 'primereact/dropdown';
import { Message } from 'primereact/message';
import { Tooltip } from 'primereact/tooltip';
import { FormEvent } from 'primereact/ts-helpers';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const StyledDiv = styled.div`
  font-weight: 400;
  font-size: var(--xsmall-font-size);
`;

const StyledInputIconButton = styled.i`
  cursor: pointer;
  transition: color 0.3s;

  &:hover {
    color: var(--red-main) !important;
  }
`;

const StyledRoleOption = styled.span`
  display: block;
  width: 100%;
  pointer-events: all;
`;

type UserFormProps = {
  initialValues: UserFormState;
  isMultiAdminRestriction?: boolean;
  state?: LoadingStateType;
  reactivateState?: LoadingStateType;
  onSubmit: (data: FormikValues) => void;
  onGoToUser?: (userId: string) => void;
  onReactivateUser?: (userId: string) => void;
  isCreate?: boolean;
  isReadOnlyView?: boolean;
  roles: Role[];
  groups?: Group[];
  disabled?: boolean;
  isSSOEnabled?: boolean;
  origin?: UserOrigin;
  syncedAdFields?: string[];
  isAdSyncedField?: boolean;
};

export const UserForm: React.FC<UserFormProps> = ({
  initialValues,
  isMultiAdminRestriction,
  state,
  reactivateState,
  onSubmit,
  onGoToUser,
  onReactivateUser,
  isCreate,
  isReadOnlyView,
  roles,
  groups,
  disabled,
  isSSOEnabled,
  origin,
  syncedAdFields,
  isAdSyncedField,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const account = useAppSelector(selectCurrentAccount);
  const user = useAppSelector(selectCurrentUser);
  const toast = useToast();
  const { can } = usePermission();
  const { canUseFeature } = useFeatureFlag();
  const isDomainVerificationEnabled = canUseFeature(
    PostHogFeatureFlags.USER_DOMAIN_VERIFICATION,
  );
  const { domains } = useAccountDomains(
    isDomainVerificationEnabled ? account?.id : undefined,
  );
  const { users: accountUsers } = useAccountUsers({
    accountId: account?.id,
    take: 1,
    enabled: !!account?.id && !!isCreate && isDomainVerificationEnabled,
  });
  const accountHasNoUsers = accountUsers?.count === 0;
  const isBranchAdmin = branchAdminCheck(user, account);
  const isDemoAccount = isDemoRestrictedMode(account, user);
  const fetchAccounts = disabled
    ? undefined
    : async (params: ListingRequest) =>
        await client.accounts.getAccounts(params);

  const [passwordVisibility, setPasswordVisibility] = useState(false);
  const [hasActiveIntegration, setHasActiveIntegration] = useState(false);
  const [existingUser, setExistingUser] = useState<UsernameLookupResult | null>(
    null,
  );

  const debouncedValidateUsername = useCallback(
    debounce(async (username: string) => {
      if (!username || !account?.id) {
        setExistingUser(null);
        return;
      }

      try {
        const found = await client.users.checkUsername(username, account.id);

        setExistingUser(found ?? null);
      } catch (e) {
        handleAxiosError(e as Error | AxiosError, toast);
      }
    }, 200),
    [account?.id, toast],
  );

  useEffect(() => {
    return () => {
      debouncedValidateUsername.cancel();
    };
  }, [debouncedValidateUsername]);

  const customValidate = () => {
    return {};
  };

  useEffect(() => {
    if (account?.activeDirectoryIntegration) {
      setHasActiveIntegration(
        !!account?.activeDirectoryIntegration?.find(
          (integration) => integration.active,
        ),
      );
    }
  }, [account]);

  useEffect(() => {
    setExistingUser(null);
  }, [initialValues.username]);

  const fetchBranches = async (params: ListingRequest) =>
    await client.branches.getBranches(
      { ...params, sort: ['name,asc'] },
      account?.id,
    );

  const hasBlockEmailsMessage = showBlockEmailsMessage(account);

  const handleCopyUsername = async (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    await navigator.clipboard.writeText(initialValues.username ?? '');
    toast?.success('', t('clipboard.copied'));
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={
        isCreate
          ? createUserSchema(t, isBranchAdmin)
          : updateUserSchema(t, isBranchAdmin, account?.meta?.isSSO)
      }
      validate={customValidate}
      onSubmit={(values) => {
        if (existingUser) return;
        onSubmit(values);
      }}
    >
      {({ values, errors, setFieldValue, setFieldTouched, isValidating }) => {
        const fieldsDisabled = disabled || !!existingUser;

        const emailDomainVerified: boolean | undefined = (() => {
          if (!isCreate && values.email === initialValues.email)
            return initialValues.domainVerified;
          const emailDomain = values.email?.split('@')[1]?.toLowerCase();
          if (!emailDomain || !domains) return undefined;
          const match = domains.find(
            (d) => d.domain.toLowerCase() === emailDomain,
          );
          return match !== undefined
            ? match.status === AccountDomainStatus.VERIFIED
            : false;
        })();

        const isDomainRoleRestricted = (roleCode: string) =>
          isDomainVerificationEnabled &&
          emailDomainVerified === false &&
          !(isCreate && accountHasNoUsers) &&
          [
            SystemRoles.ADMIN,
            SystemRoles.MULTIPLE_ACCOUNTS_ADMIN,
            SystemRoles.COMMERCIAL_ADMIN,
          ].includes(roleCode as SystemRoles);

        const modifyRoleOptions = roles
          ?.filter((role) => {
            if (account?.isSystem) {
              if (user?.role.code === SystemRoles.SUPER_ADMIN) {
                return role.code === SystemRoles.COMMERCIAL_ADMIN;
              }
              return role.code !== SystemRoles.MULTIPLE_ACCOUNTS_ADMIN;
            } else if (user?.account.isSystem) {
              return (
                role.code !== SystemRoles.DEVELOPER &&
                role.code !== SystemRoles.SUPER_ADMIN &&
                role.code !== SystemRoles.COMMERCIAL_ADMIN
              );
            } else if (!account?.isSystem) {
              return role.code !== SystemRoles.DEVELOPER &&
                role.code !== SystemRoles.SUPER_ADMIN &&
                role.code !== SystemRoles.COMMERCIAL_ADMIN &&
                disabled
                ? true
                : role.code !== SystemRoles.MULTIPLE_ACCOUNTS_ADMIN;
            } else {
              return true;
            }
          })
          .map((role) => ({
            ...role,
            name: roleTranslations(role, t),
            disabled:
              isDomainRoleRestricted(role.code as SystemRoles) || undefined,
          }));

        const roleItemTemplate = (option: typeof modifyRoleOptions[0]) => {
          if (!option) return null;
          if (option.disabled) {
            const tooltipTargetId = `role-item-${option.id}`;
            return (
              <>
                <Tooltip
                  target={`#${tooltipTargetId}`}
                  content={t('user.domain.roleRestricted')}
                  position="top"
                />
                <StyledRoleOption id={tooltipTargetId}>
                  {option.name}
                </StyledRoleOption>
              </>
            );
          }
          return <span>{option.name}</span>;
        };

        return (
          <FormContainer>
            <Form className="w-full" autoComplete="off">
              {isCreate ? (
                <div className="field w-full mb-4">
                  <Field
                    id="username"
                    label={t('generic.username')}
                    type="username"
                    name="username"
                    className="w-full"
                    component={FormikInput}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setFieldTouched('username', true);
                      const cleanedValue = e.target.value.replace(/ /g, '');

                      if (existingUser) {
                        setExistingUser(null);
                      }

                      if (cleanedValue.length >= 3) {
                        debouncedValidateUsername(cleanedValue);
                      } else {
                        debouncedValidateUsername.cancel();
                      }

                      setFieldValue('username', cleanedValue);
                    }}
                    placeholder={t('generic.username')}
                    required
                    disabled={disabled}
                    inputState={
                      existingUser
                        ? existingUser.deletedAt
                          ? 'exists-inactive'
                          : existingUser.active
                          ? 'exists-active'
                          : 'exists-inactive'
                        : undefined
                    }
                    inputStateMessage={
                      existingUser
                        ? existingUser.deletedAt
                          ? t('user.username.existsDeleted')
                          : existingUser.active
                          ? t('user.username.existsActive')
                          : t('user.username.existsInactive')
                        : undefined
                    }
                  />
                </div>
              ) : (
                <div className="field w-full mb-4">
                  <Field
                    id="username"
                    label={t('generic.username')}
                    type="username"
                    name="username"
                    className="w-full"
                    component={FormikIconInput}
                    icon={
                      values.username ? (
                        <StyledInputIconButton
                          className="pi pi-clone"
                          onClick={handleCopyUsername}
                        />
                      ) : null
                    }
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setFieldTouched('username', true);
                      const cleanedValue = e.target.value.replace(/ /g, '');

                      if (existingUser) {
                        setExistingUser(null);
                      }

                      if (
                        cleanedValue.length >= 3 &&
                        cleanedValue !== initialValues.username
                      ) {
                        debouncedValidateUsername(cleanedValue);
                      } else {
                        debouncedValidateUsername.cancel();
                      }

                      setFieldValue('username', cleanedValue);
                    }}
                    placeholder={t('generic.username')}
                    required
                    disabled={
                      user?.role.code === SystemRoles.LEARNER ||
                      origin === UserOrigin.AZURE_AD ||
                      origin === UserOrigin.GOOGLE_WORKSPACE
                    }
                    inputState={
                      existingUser
                        ? existingUser.deletedAt
                          ? 'exists-inactive'
                          : existingUser.active
                          ? 'exists-error'
                          : 'exists-inactive'
                        : undefined
                    }
                    inputStateMessage={
                      existingUser
                        ? existingUser.deletedAt
                          ? t('user.username.existsDeleted')
                          : existingUser.active
                          ? t('user.username.taken')
                          : t('user.username.existsInactive')
                        : undefined
                    }
                  />
                </div>
              )}
              <div className="field w-full mb-4">
                <Field
                  id="first-name"
                  label={t('generic.firstName')}
                  name="firstName"
                  className="w-full"
                  component={FormikInput}
                  placeholder={t('generic.firstName')}
                  required
                  disabled={fieldsDisabled}
                  isSyncedField={syncedAdFields?.includes(
                    ADEntityField.FIRST_NAME,
                  )}
                  isAdSyncedField={isAdSyncedField}
                />
              </div>
              <div className="field w-full mb-4">
                <Field
                  id="last-name"
                  label={t('generic.lastName')}
                  name="lastName"
                  className="w-full"
                  component={FormikInput}
                  placeholder={t('generic.lastName')}
                  required
                  disabled={fieldsDisabled}
                  isSyncedField={syncedAdFields?.includes(
                    ADEntityField.LAST_NAME,
                  )}
                  isAdSyncedField={isAdSyncedField}
                />
              </div>
              <div className="field w-full mb-4">
                <label htmlFor="email">
                  {t('generic.email')}
                  <span className="red"> *</span>
                </label>
                <FlexContainer align="center" justify="center" gap={8}>
                  <div className="w-full">
                    <Field
                      id="email"
                      name="email"
                      className="w-full"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setFieldValue('email', e.target.value.replace(/ /g, ''))
                      }
                      component={FormikInput}
                      placeholder={t('generic.email')}
                      disabled={fieldsDisabled || isAdSyncedField}
                    />
                  </div>
                  {isDomainVerificationEnabled &&
                    emailDomainVerified !== undefined &&
                    !errors.email && (
                      <>
                        {emailDomainVerified === false && (
                          <AppChip
                            label={t('user.domain.notVerifiedEmail')}
                            type="error"
                            size="s"
                            tooltip={t('user.domain.notVerifiedEmailTooltip')}
                            tooltipOptions={{ position: 'right' }}
                            className="cursor-pointer"
                            onClick={() =>
                              navigate(
                                account?.isSystem
                                  ? RedirectPaths[
                                      RedirectPathsEnum
                                        .ACCOUNTS_EMAILS_AND_DELIVERY_DOMAINS
                                    ](account.id)
                                  : RedirectPaths[
                                      RedirectPathsEnum
                                        .ACCOUNT_EMAILS_AND_DELIVERY_DOMAINS
                                    ](),
                              )
                            }
                          />
                        )}
                        {emailDomainVerified === true && (
                          <AppChip
                            label={t('user.domain.verifiedEmail')}
                            type="primary"
                            size="s"
                          />
                        )}
                      </>
                    )}
                </FlexContainer>
              </div>
              {!isCreate &&
                !isReadOnlyView &&
                (!isSSOEnabled || values.isManual) && (
                  <FlexContainer align="flex-start" className="mb-4">
                    <div className="field w-full mb-0">
                      <Field
                        id="password"
                        label={t('generic.newPassword')}
                        name="password"
                        type={passwordVisibility ? 'text' : 'password'}
                        className="w-full"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setFieldValue(
                            'password',
                            e.target.value.replace(/ /g, ''),
                          )
                        }
                        component={FormikInput}
                        disabled={fieldsDisabled}
                      />
                    </div>
                    <AppButton
                      icon={
                        passwordVisibility ? 'pi pi-eye' : 'pi pi-eye-slash'
                      }
                      type="text"
                      severity="secondary"
                      onClick={() => setPasswordVisibility((state) => !state)}
                      className="input-eye-button"
                    />
                  </FlexContainer>
                )}
              <div className="field w-full mb-4">
                <Field
                  id="role"
                  label={t('generic.role')}
                  name="role"
                  filter
                  onChange={(e: DropdownChangeEvent) =>
                    setFieldValue('role', e.value)
                  }
                  onBlur={() => setFieldTouched('role', true)}
                  optionLabel="name"
                  className="w-full"
                  component={FormikDropdown}
                  placeholder={t('generic.select')}
                  options={
                    isMultiAdminRestriction ? [values.role] : modifyRoleOptions
                  }
                  itemTemplate={roleItemTemplate}
                  required
                  disabled={fieldsDisabled || isMultiAdminRestriction}
                />
              </div>
              {values.role?.code === SystemRoles.MULTIPLE_ACCOUNTS_ADMIN && (
                <div className="field">
                  <label>{t('user.assignedAccounts')}</label>
                  <PartialRequestMultiselect
                    id="assignedAccounts"
                    name="assignedAccounts"
                    filter
                    dataKey="id"
                    optionLabel="name"
                    display="chip"
                    handleOnChange={(values: Account[]) =>
                      setFieldValue('assignedAccounts', values)
                    }
                    className="w-full"
                    placeholder={t('accounts.select')}
                    fetchService={fetchAccounts}
                    maxSelectedLabels={3}
                    additionalFilters={[
                      equal('active', true),
                      notEqual('id', account?.id),
                    ]}
                    selectedOptions={values?.assignedAccounts}
                    isDisabled={fieldsDisabled || isMultiAdminRestriction}
                  />
                </div>
              )}
              <div className="field w-full mb-4">
                <Field
                  id="language"
                  label={t('generic.lang')}
                  name="language"
                  filter
                  onChange={(e: DropdownChangeEvent) =>
                    setFieldValue('language', e.value)
                  }
                  onBlur={() => setFieldTouched('language', true)}
                  className="w-full"
                  component={FormikDropdown}
                  placeholder={t('generic.select')}
                  options={availableLanguagesDropdown.filter(
                    ({ value }) => !courseOnlyLanguages.includes(value),
                  )}
                  required
                  isSyncedField={syncedAdFields?.includes(
                    ADEntityField.LANGUAGE,
                  )}
                  isAdSyncedField={isAdSyncedField}
                  disabled={fieldsDisabled}
                  {...preventDropdownBrowserTranslate}
                />
              </div>
              <div className="field w-full mb-4">
                <Field
                  id="branch"
                  showClear={!isBranchAdmin}
                  label={t('branch')}
                  name="branch"
                  filter
                  onChange={(e: DropdownChangeEvent) =>
                    setFieldValue('branch', e.value)
                  }
                  onBlur={() => setFieldTouched('branch', true)}
                  optionLabel="name"
                  className="w-full"
                  component={FormikLazyDropdown}
                  placeholder={t('generic.select')}
                  fetchService={fetchBranches}
                  additionalFilters={[equal('account', account?.id)]}
                  disabled={fieldsDisabled}
                  required={isBranchAdmin}
                />
              </div>

              {can(Actions.UPDATE, Subjects.GROUPS) && !isBranchAdmin && (
                <div className="field w-full mb-4">
                  <Field
                    id="groups"
                    dataKey="id"
                    showClear={
                      !values.groups?.find((group) => group?.isForPhishing)
                    }
                    label={t('groups')}
                    name="groups"
                    filter
                    onChange={(e: DropdownChangeEvent) =>
                      setFieldValue('groups', e.value)
                    }
                    onBlur={() => setFieldTouched('groups', true)}
                    optionLabel="name"
                    className="w-full"
                    component={FormikMultiSelect}
                    maxSelectedLabels={3}
                    placeholder={t('generic.select')}
                    options={groups}
                    disabled={fieldsDisabled}
                  />
                </div>
              )}
              {!isReadOnlyView && (
                <>
                  <div className="field-checkbox">
                    <Field
                      inputId="active"
                      name="active"
                      label={t('user.setActive')}
                      component={FormikSwitch}
                      disabled={fieldsDisabled}
                    />
                  </div>

                  {!!account?.meta?.isSSO && (
                    <>
                      <div className="field-checkbox">
                        <Field
                          inputId="isManual"
                          name="isManual"
                          label={t('user.useManualLogin')}
                          component={FormikSwitch}
                          disabled={fieldsDisabled}
                        />
                      </div>
                      {!isCreate &&
                        values.isManual !== initialValues.isManual && (
                          <Message
                            severity="warn"
                            icon="pi pi-info-circle"
                            className="mt-2 mb-4"
                            text={() => (
                              <>
                                {t(
                                  values.isManual
                                    ? 'user.loginMethod.warning.toManual'
                                    : 'user.loginMethod.warning.toSSO',
                                )}
                              </>
                            )}
                          />
                        )}
                    </>
                  )}

                  {(values?.role?.code === SystemRoles.ADMIN ||
                    values?.role?.code ===
                      SystemRoles.MULTIPLE_ACCOUNTS_ADMIN) &&
                    origin !== UserOrigin.AZURE_AD && (
                      <div className="field-checkbox">
                        <Field
                          inputId="isTemporary"
                          name="isTemporary"
                          label={t('user.temporary.account')}
                          component={FormikSwitch}
                          disabled={fieldsDisabled}
                          className="flex-shrink-0"
                        />
                        {values.isTemporary && (
                          <FlexContainer
                            align="center"
                            className="ml-2"
                            width="auto"
                          >
                            <span>{t('user.temporary.account.delete.on')}</span>
                            <AppCalendar
                              id="expiresOn"
                              name="expiresOn"
                              minDate={new Date()}
                              value={
                                values.expiresOn
                                  ? moment(values.expiresOn?.toString())
                                      .utc(true)
                                      .toDate()
                                  : null
                              }
                              className="ml-2"
                              readOnlyInput
                              showButtonBar
                              disabled={fieldsDisabled}
                              onChange={(event: FormEvent) =>
                                setFieldValue(
                                  'expiresOn',
                                  moment(event.value?.toString())
                                    .utc(true)
                                    .toDate(),
                                )
                              }
                              onClearButtonClick={() => {
                                setFieldValue('expiresOn', null);
                              }}
                            />
                          </FlexContainer>
                        )}
                      </div>
                    )}

                  {!values.active && !isCreate && (
                    <div className="field w-full mb-4">
                      <label className="block">
                        {t('user.reactivates.on.date')}
                      </label>
                      <AppCalendar
                        id="reactivatesOn"
                        name="reactivatesOn"
                        minDate={new Date()}
                        value={
                          values.reactivatesOn
                            ? moment(values.reactivatesOn?.toString())
                                .utc(true)
                                .toDate()
                            : null
                        }
                        className="w-full"
                        placeholder={t('generic.select.date')}
                        readOnlyInput
                        showButtonBar
                        disabled={fieldsDisabled}
                        onChange={(event: FormEvent) =>
                          setFieldValue(
                            'reactivatesOn',
                            moment(event.value?.toString()).utc(true).toDate(),
                          )
                        }
                        onClearButtonClick={() => {
                          setFieldValue('reactivatesOn', null);
                        }}
                      />
                    </div>
                  )}

                  {!values?.manuallyDeactivated &&
                    !initialValues.active &&
                    !!hasActiveIntegration && (
                      <Message
                        severity="error"
                        text={() => <div>{t('user.ad.deactivated')}</div>}
                        icon="pi pi-info-circle"
                        className="mt-2"
                      />
                    )}

                  {values?.manuallyDeactivated &&
                    !!account?.activeDirectoryIntegration && (
                      <Message
                        severity="warn"
                        text={() => (
                          <>
                            <div>{t('user.manually.deactivated.message')}</div>
                            {values?.manuallyDeactivatedOn &&
                              values?.manuallyDeactivatedByUserName && (
                                <StyledDiv>
                                  {t('user.manually.deactivated.by', {
                                    name: values.manuallyDeactivatedByUserName,
                                    date: moment(
                                      values.manuallyDeactivatedOn,
                                    ).format(DateFormats.TIMEDATE),
                                  })}
                                </StyledDiv>
                              )}
                          </>
                        )}
                        icon="pi pi-info-circle"
                        className="mt-2"
                      />
                    )}
                  <FlexContainer gap={16} align="center" justify="flex-start">
                    {isCreate && existingUser ? (
                      <AppButton
                        severity="secondary"
                        label={
                          existingUser.deletedAt
                            ? t('button.resurrectUser')
                            : existingUser.active
                            ? t('button.goToUser')
                            : t('button.reactivateUser')
                        }
                        className="w-4 mt-4 mb-4 w-max"
                        state={reactivateState}
                        onClick={() =>
                          existingUser.deletedAt
                            ? onReactivateUser?.(existingUser.id)
                            : existingUser.active
                            ? onGoToUser?.(existingUser.id)
                            : onReactivateUser?.(existingUser.id)
                        }
                      />
                    ) : (
                      <AppButton
                        isSubmit={!isDemoAccount}
                        severity="secondary"
                        label={isCreate ? t('button.create') : t('button.save')}
                        className="w-4 mt-4 mb-4 w-max"
                        state={state}
                        isDisabled={
                          !!Object.keys(errors).length ||
                          disabled ||
                          isValidating ||
                          !!existingUser
                        }
                      />
                    )}
                    {hasBlockEmailsMessage && <InlineBlockEmails />}
                  </FlexContainer>
                </>
              )}
            </Form>
          </FormContainer>
        );
      }}
    </Formik>
  );
};
